import OriginalKeycloak, {
  KeycloakConfig,
  KeycloakError,
  KeycloakLoginOptions,
  KeycloakLogoutOptions,
} from 'keycloak-js';

import {
  defaultKeycloakInitConfig,
  getLogoutUrl,
  appendParentOriginIntoUrl,
  getUrlParamValue,
  removeUrlParam,
  winkGetCookie,
  winkEraseCookie,
  winkSetCookie,
  getWinkSessionId,
} from './helper';

type WinkInitParams = {
  onFailure?(error: unknown): void;
  onSuccess?(): void | Promise<void>;
};

type WinkKeycloakConfig = KeycloakConfig & {
  onAuthErrorFailure?(error: KeycloakError): void;
  loggingEnabled?: boolean;
};

type WinkLoginParams = KeycloakLoginOptions & {
  onFailure?(error: unknown): void;
  extraQueryParams?: Record<string, string>;
};

type WinkLogoutParams = KeycloakLogoutOptions & {
  onFailure?(error: unknown): void;
};

class CustomKeycloak extends OriginalKeycloak {
  onAuthErrorFailure?: (error: KeycloakError) => void;
  loggingEnabled: boolean = true;
  public winkSessionId: string | undefined = undefined;

  constructor(config: WinkKeycloakConfig) {
    super(config);
    this.onAuthErrorFailure = config.onAuthErrorFailure;
    this.loggingEnabled = config.loggingEnabled ?? true;
  }

  onReady(authenticated?: boolean | undefined): void {
    if (this.loggingEnabled) {
      console.debug('Keycloak client ready.');
      console.debug('Keycloak client authenticated: ', authenticated);
    }
  }

  onAuthError(errorData: KeycloakError): void {
    this.onAuthErrorFailure?.(errorData);

    if (this.loggingEnabled) {
      console.error('Keycloak client authentication error: ', errorData);
    }
  }

  // NOTE custom logout url
  createLogoutUrl = (options?: KeycloakLogoutOptions): string => {
    try {
      const logoutUrl = getLogoutUrl(this, options);

      return logoutUrl;
    } catch (error) {
      if (this.loggingEnabled) {
        console.error('Error while constructing the logout url: ', error);
      }
      throw error;
    }
  };

  winkInit = async (params?: WinkInitParams): Promise<void> => {
    const { onFailure, onSuccess } = params ?? {};
    try {
      if (this.loggingEnabled) {
        console.debug('Calling Keycloak init.');
      }
      const authenticated = await this.init(defaultKeycloakInitConfig);

      if (authenticated) {
        onSuccess?.();
        // Following line is workaround for iframe 3rd party cookie issue
        winkSetCookie('wink_id_token', this.idToken!);
      }
      // Following code is workaround for iframe 3rd party cookie issue
      else {
        // Following if is workaround for iframe 3rd party cookie issue
        if (getUrlParamValue('login_session_state') === '1') {
          this.winkLogin();
        }
      }
    } catch (error) {
      if (this.loggingEnabled) {
        console.error('Error while wink client initialization: ', error);
      }
      onFailure?.(error);
    }
  };

  winkLogin = async (params?: WinkLoginParams): Promise<void> => {
    const { onFailure } = params ?? {};
    try {
      if (this.loggingEnabled) {
        console.debug('Calling Keycloak login.');
      }

      // Get fresh sessionId only when login button is clicked
      this.winkSessionId = await this.getWinkSessionIdForLogin();

      await this.login(params);
    } catch (error) {
      if (this.loggingEnabled) {
        console.error('Error while wink client login: ', error);
      }

      onFailure?.(error);
    }
  };

  // Method to get sessionId for login (called only when login button is clicked)
  getWinkSessionIdForLogin = async (): Promise<string> => {
    if (this.loggingEnabled) {
      console.debug('Getting sessionId for login...');
    }

    const returnUrl = window.location.href;
    const config = (window as any).winkEnvValues?.winkApi;
    const cancelUrl = config?.cancelUrl || 'https://example.com/callback';

    const sessionId = await getWinkSessionId(returnUrl, cancelUrl, this.loggingEnabled);

    if (!sessionId) {
      throw new Error('Failed to obtain session ID from Wink API');
    }

    if (this.loggingEnabled) {
      console.debug('SessionId obtained for login:', sessionId);
    }

    return sessionId;
  };

  winkLogout = async (params?: WinkLogoutParams): Promise<void> => {
    const { onFailure } = params ?? {};
    try {
      if (this.loggingEnabled) {
        console.debug('Calling Keycloak logout.');
      }
      // Following code is workaround for iframe 3rd party cookie issue
      this.idToken = this.idToken ?? winkGetCookie('wink_id_token');
      winkEraseCookie('wink_id_token');
      removeUrlParam('login_session_state');
      // End of workaround

      await this.logout(params);
    } catch (error) {
      if (this.loggingEnabled) {
        console.error('Error while wink client logout: ', error);
      }

      onFailure?.(error);
    }
  };
}

// Custom login client with sessionId support via extraQueryParams
const getWinkLoginClient = (config: WinkKeycloakConfig) => {
  const keycloakInstance = new CustomKeycloak(config);

  const originalCreateLoginUrl = keycloakInstance.createLoginUrl;

  keycloakInstance.createLoginUrl = function (options?: KeycloakLoginOptions) {
    // Add sessionId to extraQueryParams if available
    const modifiedOptions = { ...options } as any;
    if (this.winkSessionId) {
      modifiedOptions.extraQueryParams = {
        ...(options as any)?.extraQueryParams,
        sessionId: this.winkSessionId,
      };
    }

    const url = originalCreateLoginUrl.call(this, modifiedOptions);

    // Add sessionId directly to URL if not present
    let finalUrl = url;
    if (this.winkSessionId && !url.includes('sessionId=')) {
      const separator = url.includes('?') ? '&' : '?';
      finalUrl = url + separator + 'sessionId=' + encodeURIComponent(this.winkSessionId);
    }

    const urlWithParentOrigin = appendParentOriginIntoUrl(finalUrl, config.loggingEnabled ?? true);

    return urlWithParentOrigin;
  };

  return keycloakInstance;
};

console.info('winklogin.module.js - version 2.1.1');

//@ts-ignore
window.getWinkLoginClient = getWinkLoginClient;
